import { apiClient } from "../index";

const MY_OVERVIEW_PATH = "/api/v1/dashboard/professional/my-overview";
const TOKEN_ACTIVITY_PATH = "/api/v1/dashboard/professional/token-activity";
const SOLO_PROGRESSION_PATH =
  "/api/v1/dashboard/professional/solo-progression";
const CATEGORY_COVERAGE_PATH =
  "/api/v1/dashboard/professional/category-coverage";
const DIFFICULTY_STATS_PATH =
  "/api/v1/dashboard/professional/difficulty-stats";
const ACTIVITY_DEPLOYMENTS_PATH =
  "/api/v1/dashboard/professional/activity-deployments";

/* ─────────────────────────────────────────────
   Section 1 — My Overview
   ───────────────────────────────────────────── */

export async function getMyOverview() {
  return apiClient.get(MY_OVERVIEW_PATH);
}

/**
 * Normalize the raw payload into the flat shape the four overview cards expect.
 */
export function normalizeMyOverview(raw) {
  const comp = raw?.completion ?? {};
  const fts = raw?.first_try_success ?? {};
  const bc = raw?.boxes_created ?? {};
  const tok = raw?.tokens ?? {};

  return {
    /* Boxes Completed card */
    boxesCompleted: comp.completed ?? 0,
    boxesAttempted: comp.attempted ?? 0,

    /* First-Try Success card */
    firstTrySuccess: fts.percentage ?? 0,

    /* Boxes Requested card — scenario-creation flow runs.
       The API reports only total / successful / failed. `total` counts
       every run regardless of state, and successful (Completed) + failed
       (Failed|Crashed|TimedOut) do not cover the unresolved states
       (Pending / Scheduled / Submitted / Running), so the remainder is
       derived here rather than returned by the endpoint. */
    boxesCreated: bc.total ?? 0,
    created: {
      successful: bc.successful ?? 0,
      failed: bc.failed ?? 0,
      inProgress: Math.max(
        0,
        (bc.total ?? 0) - (bc.successful ?? 0) - (bc.failed ?? 0),
      ),
    },

    /* Tokens Remaining card */
    tokensUsedToday: tok.used_today ?? 0,
    dailyCap: tok.daily_cap ?? 0,
    tokensRemaining: tok.remaining ?? 0,
  };
}

/* ─────────────────────────────────────────────
   Section 2 — Token Activity
   ───────────────────────────────────────────── */

export async function getTokenActivity() {
  return apiClient.get(TOKEN_ACTIVITY_PATH);
}

/**
 * Normalize token-activity into the shape the trend chart + stat tiles expect.
 */
export function normalizeTokenActivity(raw) {
  const chart = raw?.usage_30d_chart ?? {};
  const labels = chart.labels ?? [];
  const series =
    chart.datasets?.find((d) => d.label === "Tokens")?.data ??
    chart.datasets?.[0]?.data ??
    [];

  const total = raw?.tokens_last_30_days ?? 0;
  const avg = raw?.daily_avg ?? 0;
  const today = raw?.tokens_today ?? 0;
  const conversations = raw?.total_conversations ?? 0;
  const totalAllTime = raw?.total_tokens ?? 0;

  const peakVal = Math.max(...series, 0);
  const peakIdx = series.indexOf(peakVal);

  return {
    total,
    avg,
    today,
    conversations,
    totalAllTime,
    peakIdx: peakIdx >= 0 ? peakIdx : series.length - 1,

    labels: labels.map((iso) => {
      const d = new Date(iso + "T00:00:00");
      return d.toLocaleDateString("en", { month: "short", day: "numeric" });
    }),
    series,
  };
}

/* ─────────────────────────────────────────────
   Section 3 — Solo Box Progression
   ───────────────────────────────────────────── */

export async function getSoloProgression() {
  return apiClient.get(SOLO_PROGRESSION_PATH);
}

/**
 * Normalize solo-progression into:
 *  • chart labels (formatted) + two cumulative series (attempted, completed)
 *  • completion overview scalars + derived percentage
 */
export function normalizeSoloProgression(raw) {
  const chart = raw?.attempt_history_7d_chart ?? {};
  const labels = chart.labels ?? [];
  const datasets = chart.datasets ?? [];

  const attemptedDaily =
    datasets.find((d) => d.label === "Attempted")?.data ??
    datasets[0]?.data ??
    [];
  const completedDaily =
    datasets.find((d) => d.label === "Completed")?.data ??
    datasets[1]?.data ??
    [];

  /* Accumulate daily values into running totals for the cumulative line */
  const cumulate = (arr) => {
    let sum = 0;
    return arr.map((v) => (sum += v));
  };

  const co = raw?.completion_overview ?? {};
  const completed = co.completed ?? 0;
  const attempted = co.attempted ?? 0;
  const untouched = co.untouched ?? 0;
  const total = completed + attempted + untouched;
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;

  return {
    labels: labels.map((iso) => {
      const d = new Date(iso + "T00:00:00");
      return d.toLocaleDateString("en", { month: "short", day: "numeric" });
    }),
    attemptedCum: cumulate(attemptedDaily),
    completedCum: cumulate(completedDaily),

    completion: {
      completed,
      attempted,
      untouched,
      pct,
    },
  };
}

/* ─────────────────────────────────────────────
   Section 4 — Category Coverage & Recommendations
   ───────────────────────────────────────────── */

export async function getCategoryCoverage() {
  return apiClient.get(CATEGORY_COVERAGE_PATH);
}

/**
 * Extract a short display name from the long "Name - Description …" labels
 */
function shortName(full = "") {
  const idx = full.indexOf(" - ");
  return idx > 0 ? full.slice(0, idx).trim() : full;
}

/**
 * Normalize category-coverage into:
 *  • chart labels + values (already sorted desc by completion %)
 *  • per-category detail rows
 *  • mastered / untouched name lists (short names)
 */
export function normalizeCategoryCoverage(raw) {
  const chart = raw?.completion_by_category_chart ?? {};
  const labels = chart.labels ?? [];
  const values =
    chart.datasets?.find((d) => d.label === "Completion %")?.data ??
    chart.datasets?.[0]?.data ??
    [];

  const categories = (raw?.categories ?? []).map((c) => ({
    id: c.category_id,
    name: shortName(c.name),
    fullName: c.name ?? "",
    total: c.total ?? 0,
    completed: c.completed ?? 0,
    pct: c.completion_pct ?? 0,
  }));

  return {
    chart: {
      labels: labels.map(shortName),
      values,
    },
    categories,
    mastered: (raw?.fully_mastered ?? []).map(shortName),
    untouched: (raw?.untouched ?? []).map(shortName),
  };
}

/* ─────────────────────────────────────────────
   Section 5 — Difficulty Stats
   ───────────────────────────────────────────── */

export async function getDifficultyStats() {
  return apiClient.get(DIFFICULTY_STATS_PATH);
}

/** Format minutes into a compact human label: "2.6 min", "1h 12m", "—" */
function fmtMinutes(m) {
  if (!m || m <= 0) return "—";
  if (m < 60) return `${Math.round(m * 10) / 10} min`;
  const h = Math.floor(m / 60);
  const rem = Math.round(m % 60);
  return rem > 0 ? `${h}h ${rem}m` : `${h}h`;
}

/**
 * Normalize difficulty-stats into an ordered [easy, medium, hard] array
 * with derived display values.
 */
export function normalizeDifficultyStats(raw) {
  const buckets = raw?.buckets ?? [];

  const ORDER = ["easy", "medium", "hard"];
  const mapped = ORDER.map((diff) => {
    const b = buckets.find((x) => x.difficulty === diff) ?? {};
    return {
      difficulty: diff,
      completed: b.completed ?? 0,
      avgMinutes: b.avg_completion_minutes ?? 0,
      avgLabel: fmtMinutes(b.avg_completion_minutes),
    };
  });

  const totalCompleted = mapped.reduce((s, b) => s + b.completed, 0);

  return { buckets: mapped, totalCompleted };
}

/* ─────────────────────────────────────────────
   Section 6 — Activity & Deployments
   ───────────────────────────────────────────── */

export async function getActivityDeployments() {
  return apiClient.get(ACTIVITY_DEPLOYMENTS_PATH);
}

/**
 * Normalize activity-deployments into:
 *  • daily completed / failed flow-run series (labels formatted "Mon D")
 *  • box-request provisioning summary for the doughnut (requested / completed
 *    / failed + rounded success rate)
 */
export function normalizeActivityDeployments(raw) {
  const chart = raw?.flow_runs_30d_chart ?? {};
  const labels = chart.labels ?? [];
  const datasets = chart.datasets ?? [];
  const completedSeries =
    datasets.find((d) => d.label === "Completed")?.data ??
    datasets[0]?.data ??
    [];
  const failedSeries =
    datasets.find((d) => d.label === "Failed")?.data ??
    datasets[1]?.data ??
    [];

  const br = raw?.box_requests ?? {};

  return {
    daily: {
      labels: labels.map((iso) => {
        const d = new Date(iso + "T00:00:00");
        return d.toLocaleDateString("en", { month: "short", day: "numeric" });
      }),
      completed: completedSeries,
      failed: failedSeries,
    },
    summary: {
      requested: br.requested ?? 0,
      completed: br.completed ?? 0,
      failed: br.failed ?? 0,
      successRate: Math.round(br.success_rate_pct ?? 0),
    },
  };
}
